import LeadForm from "../components/LeadForm";
import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import {
  Brain,
  CheckCircle2,
  Dumbbell,
  Heart,
  MapPin,
  Shield,
  Star,
} from "lucide-react";
import { motion } from "motion/react";

const benefits = [
  { icon: Shield, text: "Build real self-defense skills" },
  { icon: Dumbbell, text: "Improve fitness and mobility" },
  { icon: Brain, text: "Develop mental toughness and discipline" },
  { icon: Heart, text: "Learn to stay calm under pressure" },
  { icon: Star, text: "Gain confidence on and off the mats" },
];

const whoItsFor = [
  { icon: CheckCircle2, label: "Complete beginners" },
  { icon: Dumbbell, label: "Adults looking to get in shape" },
  { icon: Star, label: "Competitors" },
  { icon: Shield, label: "Anyone wanting confidence and self-defense" },
];

const serviceAreas = [
  "Springfield",
  "Eugene",
  "Thurston",
  "Glenwood",
  "Fairmont",
];

const approachSteps = [
  {
    num: "01",
    title: "Structured Classes",
    desc: "Every session follows a proven format: warm-up, technique instruction, drilling, and live training. You always know what to expect and can focus on improving.",
  },
  {
    num: "02",
    title: "Technique + Live Training",
    desc: "We balance sharp technical instruction with real sparring. You won't just learn moves — you'll know how and when to use them under pressure.",
  },
  {
    num: "03",
    title: "Consistent Progression",
    desc: "Our framework helps you build on every session. Problem-solving and adaptability are core to how we teach, ensuring you improve week over week.",
  },
];

export default function JiuJitsuPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Jiu Jitsu Classes in Springfield, OR | Superhero Jiu Jitsu Academy
      </title>
      <meta
        name="description"
        content="Learn No-Gi Jiu-Jitsu at Superhero Jiu Jitsu Academy in Springfield, OR. We focus on confidence, control, and practical application. Each session helps you build problem-solving skills and composure under pressure."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="jj-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/assets/images/adult jiu-jitsu hero image.jpg')",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, oklch(0.15 0.045 255 / 0.92) 0%, oklch(0.15 0.045 255 / 0.65) 55%, oklch(0.15 0.045 255 / 0.25) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                Superhero Jiu Jitsu Academy · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                Jiu Jitsu in <span className="text-gold">Springfield, OR</span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Build confidence, control, and real-world self-defense skills in
                a supportive, beginner-friendly environment.
              </p>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
              data-ocid="jj-hero-form"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
              <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                Book Your Free Class
              </h2>
              <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                No experience needed. Spots fill fast — reserve yours now.
              </p>

              <LeadForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="jj-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                No Experience Required
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                Train Smarter. Build Real Skills.
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  You will learn No-Gi Jiu-Jitsu in a way that helps you think
                  on the mats while building confidence, control, and
                  problem-solving skills with every roll. Our approach helps you
                  develop your own style within a proven framework so you are
                  prepared for competition and self-defense in a fun,
                  challenging environment.
                </p>
                <p>
                  Whether you are new to Jiu-Jitsu or ready to level up your
                  game, training will help you unlock your potential. Each class
                  is structured so you leave better than you came, supported by
                  a community that pushes you to improve while keeping training
                  engaging. Discover why this approach produces creative,
                  adaptable grapplers who are ready for the challenges they face
                  on and off the mats.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_Train Smarter. Build Real Skills.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="jj-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              What You Gain
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              Why Train Jiu Jitsu?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map(({ icon: Icon, text }, i) => (
              <motion.div
                key={text}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5"
              >
                <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <p className="font-body font-medium text-sm text-foreground leading-snug">
                  {text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING APPROACH SECTION ── */}
      <section
        className="bg-background py-16 px-4 border-t border-border"
        data-ocid="jj-approach"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Our Method
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance">
              Train with Purpose
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-hero rounded-2xl overflow-hidden shadow-elevated"
          >
            <div className="p-8 sm:p-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {approachSteps.map(({ num, title, desc }, i) => (
                  <motion.div
                    key={num}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    className="text-center"
                  >
                    <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                      {num}
                    </span>
                    <h3 className="font-display font-bold text-lg text-hero-foreground mb-3">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                      {desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── WHO IT'S FOR SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="jj-who"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Everyone Is Welcome
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-10 text-balance">
              WHO IT'S FOR
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whoItsFor.map(({ icon: Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-card rounded-xl border border-border p-5 flex items-center gap-4 shadow-card text-left"
              >
                <span className="w-10 h-10 rounded-lg bg-gradient-accent flex items-center justify-center flex-shrink-0 shadow-gold">
                  <Icon className="w-4 h-4 text-foreground" />
                </span>
                <p className="font-body font-medium text-sm text-foreground">
                  {label}
                </p>
              </motion.div>
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 text-muted-foreground font-body text-sm"
          >
            Our diverse community includes students of all ages and fitness
            levels. No prior experience needed — just show up ready to learn.
          </motion.p>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="bg-hero py-20 px-4 text-center" data-ocid="jj-cta">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Limited Spots Available
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
              Start Your Jiu Jitsu Journey Today
            </h2>
            <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
              Your first class is completely free. Come in, meet the team, and
              experience the training for yourself — no commitment required.
            </p>
            <p className="text-hero-foreground font-body text-sm mb-8 opacity-80">
              📌 Must book in advance to reserve your spot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => router.navigate({ to: "/contact-us" })}
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
                data-ocid="jj-cta-button"
              >
                Book Your Free Class
              </button>
              <a
                href="tel:+15416003371"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
                data-ocid="jj-cta-phone"
              >
                Call (541) 600-3371
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="jj-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving the Springfield Area
              </span>
            </div>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto">
              Jiu Jitsu classes in Springfield, OR serving{" "}
              {serviceAreas.join(", ")}, and surrounding areas. Located at 1485
              Mohawk Blvd, Springfield, OR 97477. Call us at{" "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>{" "}
              or email{" "}
              <a
                href="mailto:shjjoffice@gmail.com"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                shjjoffice@gmail.com
              </a>
              .
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
